import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Pause, XCircle } from "lucide-react";
import { ModelAlertBanner } from "./panel/ui";
import { api, errMsg } from "@/lib/api";
import { StatusPill } from "@/pages/admin/panel/ui";
import { DiscountCodesCard } from "@/pages/admin/panel/DiscountCodesCard";
import { MailTestCard } from "@/pages/admin/panel/MailTestCard";
import { Stepper } from "./hub/Stepper";

const FILTERS = [["all", "All"], ["waiting", "Waiting on me"], ["running", "Running"], ["failed", "Failed"], ["live", "Live"]];
const matches = (r, k) => k === "all" || (k === "live" ? r.status === "published" : r.kind === k);
const NOW_STYLE = { waiting: "text-amber-700", failed: "text-red-700", running: "text-sky-700", live: "text-emerald-700", idle: "text-zinc-500" };

const STAGE_COLORS = { running: "bg-sky-50 text-sky-700 border-sky-200", queued: "bg-zinc-100 text-zinc-600 border-zinc-200", paused: "bg-amber-50 text-amber-700 border-amber-200" };
const LIVE = ["queued", "running", "paused"];

const hbAge = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`;
};

export default function JobDashboard() {
  const [certs, setCerts] = useState([]);
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [c, j, o] = await Promise.all([api.get("/admin/certs"), api.get("/admin/jobs"), api.get("/admin/overview")]);
      setCerts(c.data.certs); setJobs(j.data.jobs); setRows(o.data.certs);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const act = async (id, action, ok) => {
    try { await api.post(`/admin/jobs/${id}/${action}`); toast.success(ok); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // §9 v1.12.6.7: the Dashboard is the single LIVE view — finished jobs live in their cert's history
  const live = jobs.filter((j) => LIVE.includes(j.status));

  return (
    <div className="fade-in">
      <ModelAlertBanner />
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-black">Control Panel</h1>
        <Link to="/admin/wizard" data-testid="new-cert-btn" className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150">+ New cert</Link>
      </div>

      <div className="flex flex-wrap gap-1 mb-3" data-testid="cert-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`cert-filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-150 ${filter === k ? "bg-sky-500 text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"}`}>
            {label}{k !== "all" && <span className="ml-1 text-xs opacity-70">{rows.filter((r) => matches(r, k)).length}</span>}
          </button>
        ))}
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100" data-testid="cert-cards">
        {rows.filter((r) => matches(r, filter)).map((r) => (
          <Link key={r.id} to={`/admin/certs/${r.id}`} data-testid={`cert-card-${r.slug}`}
                className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors duration-150">
            <div className="min-w-[200px]">
              <p className="font-bold text-[15px]" data-testid={`cert-link-${r.slug}`}>{r.name}</p>
              <p className="text-xs text-zinc-400">{r.vendor}</p>
            </div>
            <StatusPill status={r.status} />
            <Stepper certId={r.id} stages={r.stages} current={r.now?.stage?.key} compact />
            <p className={`text-sm ml-auto ${NOW_STYLE[r.kind]}`} data-testid={`cert-now-${r.slug}`}>
              {r.kind === "waiting" ? "Waiting on you: " : r.kind === "failed" ? "Failed: " : r.kind === "running" ? "Machine working: " : ""}
              {r.kind === "running" ? (r.now?.stage?.line || "") : (r.now?.action || "")}
            </p>
          </Link>
        ))}
        {rows.length === 0 && <p className="text-zinc-400 text-sm py-8 text-center">No certs yet — run the Setup Wizard.</p>}
      </div>

      <h2 className="text-lg font-bold mt-8 mb-3">Live jobs</h2>
      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100" data-testid="jobs-list">
        {live.map((j) => (
          <div key={j.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`live-job-${j.id}`}>
            <span className={`px-2 py-0.5 rounded border text-xs font-mono ${STAGE_COLORS[j.status] || STAGE_COLORS.queued}`}>{j.status}</span>
            <span className="font-semibold" data-testid={`live-job-label-${j.id}`}>
              {[j.label?.type || j.type, j.label?.cert_name || j.cert_name, j.label?.app_version]
                .filter(Boolean).join(" · ")}
            </span>
            <span className="font-mono text-xs text-zinc-500">
              stage {j.stage}
              {j.questions_total != null && ` · ${j.questions_written ?? 0}/${j.questions_total}`}
              {` · beat ${hbAge(j.heartbeat)}`}
              {j.run_spend_usd != null && ` · $${j.run_spend_usd.toFixed(2)}`}
            </span>
            {(j.flags || []).slice(-2).map((f) => <span key={f} className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{f}</span>)}
            <span className="ml-auto flex items-center gap-2">
              {j.status !== "paused" && (
                <button data-testid={`job-pause-${j.id}`} onClick={() => act(j.id, "pause", "Job paused")}
                        className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors duration-150"><Pause size={12} /> pause</button>
              )}
              <button data-testid={`job-restart-${j.id}`} onClick={() => act(j.id, "restart", "Job requeued")}
                      className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 transition-colors duration-150"><RotateCcw size={12} /> restart</button>
              <button data-testid={`job-cancel-${j.id}`} onClick={() => act(j.id, "cancel", "Job cancelled")}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors duration-150"><XCircle size={12} /> cancel</button>
            </span>
          </div>
        ))}
        {live.length === 0 && <p className="px-4 py-6 text-center text-zinc-400 text-sm">No live jobs. Finished runs are in each cert's Job History.</p>}
      </div>
      <div className="mt-6"><DiscountCodesCard certs={certs} /></div>
      <div className="mt-6"><MailTestCard /></div>
    </div>
  );
}
